// Manual-mode control panel.
// - Advance-time controls (input + shortcut buttons).
// - Per-asset table: Modify, plus one GET button per user.
// - Per-user, per-asset freshness pill (fresh / stale / miss) using the
//   client cache freshness snapshot.

export function renderManualPanel(container, cfg, snapshot, handlers) {
  container.innerHTML = '';
  if (cfg.traffic.model !== 'manual') {
    container.hidden = true;
    return;
  }
  container.hidden = false;

  const title = document.createElement('div');
  title.className = 'manual-title';
  title.textContent = 'Manual controls';
  container.appendChild(title);

  const help = document.createElement('div');
  help.className = 'manual-help';
  help.textContent = 'Advance sim time to age freshness. Click Modify to bump an asset\u2019s version. GET issues a request as that user.';
  container.appendChild(help);

  // --- Advance-time strip ---
  const strip = document.createElement('div');
  strip.className = 'advance-strip';
  const label = document.createElement('span'); label.className = 'strip-label'; label.textContent = 'Advance';
  strip.appendChild(label);
  const inp = document.createElement('input');
  inp.type = 'number'; inp.min = '0'; inp.step = '1'; inp.value = '10'; inp.className = 'advance-input';
  inp.title = 'Seconds';
  strip.appendChild(inp);
  const goBtn = document.createElement('button');
  goBtn.className = 'mbtn'; goBtn.textContent = 'Go';
  goBtn.addEventListener('click', () => {
    const s = Math.max(0, Number(inp.value) || 0);
    if (s > 0) handlers.onAdvance(s);
  });
  strip.appendChild(goBtn);
  for (const s of [1, 5, 30, 60, 300]) {
    const b = document.createElement('button');
    b.className = 'mbtn shortcut'; b.textContent = `+${s < 60 ? s + 's' : (s / 60) + 'm'}`;
    b.addEventListener('click', () => handlers.onAdvance(s));
    strip.appendChild(b);
  }
  container.appendChild(strip);

  // --- Asset table ---
  const assets = snapshot?.assets ?? deriveAssets(cfg);
  const users = snapshot?.userCaches ?? deriveUsers(cfg).map((u) => ({ id: u.id, assetCache: [] }));

  const now = snapshot?.t ?? 0;
  const maxAge = cfg.client.maxAgeSec.asset;
  const ttl = cfg.client.ttlSec.asset;
  const fresh = (u, url) => freshnessFor(u, url, now, ttl);

  const table = document.createElement('table');
  table.className = 'manual-table';
  const thead = document.createElement('thead');
  const hr = document.createElement('tr');
  hr.appendChild(th('Asset'));
  hr.appendChild(th('Modify'));
  for (const u of users) hr.appendChild(th(u.id));
  thead.appendChild(hr);
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  for (const a of assets) {
    const tr = document.createElement('tr');
    const nameCell = document.createElement('td');
    nameCell.className = 'aname';
    nameCell.textContent = `${a.name} @v${a.currentVersion ?? 0}`;
    tr.appendChild(nameCell);

    const modCell = document.createElement('td');
    const modBtn = document.createElement('button');
    modBtn.className = 'mbtn mod';
    modBtn.textContent = 'Modify \u2192 v' + ((a.currentVersion ?? 0) + 1);
    modBtn.addEventListener('click', () => handlers.onModify(a.id));
    modCell.appendChild(modBtn);
    tr.appendChild(modCell);

    for (const u of users) {
      const c = document.createElement('td');
      c.className = 'req-cell';
      const btn = document.createElement('button');
      btn.className = 'mbtn req';
      btn.textContent = 'GET';
      btn.title = `${u.id} GET ${a.url ?? '/' + a.name}`;
      btn.addEventListener('click', () => handlers.onRequest(u.id, a.id));

      const pill = document.createElement('span');
      pill.className = 'fresh-pill';
      const url = a.url ?? '/' + a.name;
      const f = fresh(u, url);
      pill.classList.add(f.state);
      pill.title = f.title;
      pill.textContent = f.label;

      c.appendChild(btn);
      c.appendChild(pill);
      tr.appendChild(c);
    }
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  container.appendChild(table);
}

function th(text) { const t = document.createElement('th'); t.textContent = text; return t; }

function freshnessFor(user, url, now, ttl) {
  const cache = user.assetCache ?? [];
  const c = cache.find((x) => x.url === url);
  if (!c) return { state: 'miss', label: 'no cache', title: 'Cache miss: next GET will fetch from network.' };
  if (now - c.storedAt > ttl) return { state: 'miss', label: 'evicted', title: 'Entry exceeded TTL and will be evicted.' };
  if (now < c.freshUntil) {
    const left = Math.max(0, c.freshUntil - now);
    return { state: 'fresh', label: `fresh ${fmtDur(left)}`, title: `Served from cache for another ${fmtDur(left)}. Cached version v${c.version}, etag ${short(c.etag)}.` };
  }
  const overdue = Math.max(0, now - c.freshUntil);
  return { state: 'stale', label: `stale +${fmtDur(overdue)}`, title: `Freshness expired ${fmtDur(overdue)} ago. Next GET will revalidate with If-None-Match (etag v${c.version}).` };
}

function fmtDur(s) {
  if (s < 1) return s.toFixed(1) + 's';
  if (s < 60) return Math.floor(s) + 's';
  if (s < 3600) return Math.floor(s / 60) + 'm' + Math.floor(s % 60) + 's';
  return Math.floor(s / 3600) + 'h';
}
function short(h) { return h ? h.slice(0, 6) + '…' : '—'; }

function deriveAssets(cfg) {
  const names = ['app.js', 'vendor.js', 'app.css', 'data.json', 'runtime.wasm', 'i18n.json', 'sprite.svg', 'catalog.json'];
  const out = [];
  for (let i = 0; i < cfg.assets.count; i++) {
    out.push({ id: `a${i}`, name: names[i % names.length], currentVersion: 0, url: '/' + names[i % names.length] });
  }
  return out;
}
function deriveUsers(cfg) {
  const out = [];
  for (let i = 0; i < cfg.world.userCount; i++) out.push({ id: `u${i}` });
  return out;
}
